import React from "react";
import { useLanguage } from "../../Language";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { VirtualShelfView } from "@tlc/ls2pac/app/views/VirtualShelfView";
import { useDialog } from "@tlc/ui/dialog/useDialog";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { TLCEvents } from "../../../events/events";
import { LS2PAC_CONSTANTS } from "src/main/resources/js/main/constants";
import { useAnalytics } from "../../../analytics/useGoogleAnalytics";

interface VirtualShelfDialogProps {
  resource: ResourceDWR;
  open: boolean;
  onClose: () => void;
}

export default function VirtualShelfDialog({
  resource,
  open,
  onClose,
}: VirtualShelfDialogProps) {
  const { translate } = useLanguage();

  const fullScreen = useIsMobile();
  const { trackEvent } = useAnalytics();

  const viewRef = React.useRef<VirtualShelfView | null>(null);
  const pendingContentRef = React.useRef<string | null>(null);

  const containerNodeRef = React.useRef<HTMLDivElement | null>(null);

  const [isLoading, setIsLoading] = React.useState(true);

  const containerRefCallback = React.useCallback(
    (node: HTMLDivElement | null) => {
      containerNodeRef.current = node;
      if (node && pendingContentRef.current && viewRef.current) {
        node.innerHTML = pendingContentRef.current;
        pendingContentRef.current = null;
        viewRef.current.hasEvents = false;
        viewRef.current.updateVirtualShelf();
        viewRef.current.addDomEventsForVirtualShelf();
      }
    },
    [open],
  );

  React.useEffect(() => {
    if (open && resource && viewRef.current) {
      viewRef.current.requestVirtualShelfResources({ resource });
    }
  }, [open, resource]);

  React.useEffect(() => {
    const view = new VirtualShelfView();
    viewRef.current = view;

    view.setUpVirtualShelfDialog = (content: string) => {
      const node = containerNodeRef.current;

      if (node) {
        node.innerHTML = content;
        view.hasEvents = false;
        view.updateVirtualShelf();
        view.addDomEventsForVirtualShelf();
      } else {
        pendingContentRef.current = content;
      }

      view.isOpen = true;
      view.isOpening = false;
      setIsLoading(false);
    };

    view.close = () => {
      view.isOpen = false;
      view.enableVirtualShelfLink();
      onClose();
    };

    view.getURLParamsForHash = (hashObject: any) => {
      const url = VirtualShelfView.prototype.getURLParamsForHash.call(
        view,
        hashObject,
      );
      view.isOpen = false;
      view.enableVirtualShelfLink();
      onClose();
      return url;
    };

    return () => {
      view.cleanup();
      viewRef.current = null;
    };
  }, []);

  const handleClose = () => {
    if (viewRef.current) {
      viewRef.current.close();
    } else {
      onClose();
    }
    trackEvent("virtual_shelf_close");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>{translate("virtualShelf")}</DialogTitle>
      <DialogContent
        className="virtualShelfDialogContainer"
        sx={{ overflowX: "auto" }}
      >
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        <div
          ref={containerRefCallback}
          style={{ display: isLoading ? "none" : "block" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{translate("close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
